import { resolve, type Source, SourceSchema } from '@/agent/refs';
import { getMapView } from '@/map/mapStore';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  resolveFieldName,
  resolveFieldList,
  translateAttributes,
} from './shared/layers';
import { nextChartId, useWidgetStore } from '@/state/widgetStore';
import type { Tool } from './types';

type ChartType = 'pie' | 'donut' | 'bar' | 'line' | 'scatter' | 'histogram';
type StatsOp = 'count' | 'sum' | 'avg' | 'min' | 'max';

type Input = {
  source: Source | string;
  chart_type: ChartType;
  group_by?: string;
  stats_field?: string;
  stats_op?: StatsOp;
  out_fields?: string[];
  title?: string;
  hint?: string;
};

export const chart: Tool<Input> = {
  name: 'chart',
  description:
    'Open a chart card over the map. For category breakdowns (pie/bar/donut) pass group_by + stats_field + stats_op (use the OID with stats_op="count" for plain row counts per category). For two-numeric (scatter/line) pass out_fields. For histogram pass a single numeric out_field.',
  input_schema: {
    type: 'object',
    properties: {
      source: SourceSchema,
      chart_type: { enum: ['pie', 'donut', 'bar', 'line', 'scatter', 'histogram'] },
      group_by: { type: 'string' },
      stats_field: { type: 'string' },
      stats_op: { enum: ['count', 'sum', 'avg', 'min', 'max'] },
      out_fields: { type: 'array', items: { type: 'string' } },
      title: { type: 'string' },
      hint: { type: 'string' },
    },
    required: ['source', 'chart_type'],
    additionalProperties: false,
  },
  execute: async (input) => {
    const spec = await unwrapFeatureSet(input.source);
    const layer = await ensureFeatureLayer(spec.layer_id);
    const probe = requireLayerRecord(spec.layer_id).probe;
    const oidField = resolveFieldName(layer.objectIdField ?? 'objectid', probe, layer)
      ?? layer.objectIdField
      ?? 'objectid';
    const groupByField = resolveFieldName(input.group_by, probe, layer);
    const statsField = resolveFieldName(input.stats_field, probe, layer);

    const viewExtent = spec.in_current_extent ? getMapView()?.extent : undefined;
    if (spec.in_current_extent && !viewExtent) {
      throw new Error('chart: in_current_extent set but the map view has no extent yet');
    }

    let rows: Array<Record<string, unknown>>;
    if (input.group_by && input.stats_op) {
      if (!groupByField) {
        throw new Error(`chart: unknown group_by field "${input.group_by}"`);
      }
      const onField = input.stats_op === 'count'
        ? statsField ?? oidField
        : statsField ?? groupByField;
      const rs = await layer.queryFeatures({
        where: spec.where,
        outStatistics: [
          {
            statisticType: input.stats_op,
            onStatisticField: onField,
            outStatisticFieldName: '_v',
          },
        ],
        groupByFieldsForStatistics: [groupByField],
        geometry: viewExtent,
        spatialRelationship: viewExtent ? 'intersects' : undefined,
      });
      rows = rs.features.map((f) => translateAttributes(f.attributes ?? {}, probe));
    } else {
      const rs = await layer.queryFeatures({
        where: spec.where,
        outFields: resolveFieldList(input.out_fields, probe, layer) ?? ['*'],
        returnGeometry: false,
        num: 5000,
        geometry: viewExtent,
        spatialRelationship: viewExtent ? 'intersects' : undefined,
      });
      rows = rs.features.map((f) => translateAttributes(f.attributes ?? {}, probe));
    }

    const id = nextChartId();
    const title = input.title ?? `${spec.layer_id}: ${input.chart_type}`;
    useWidgetStore.getState().addChart({
      id,
      title,
      chart_type: input.chart_type,
      rows,
      group_by: groupByField ?? input.group_by,
      stats_field: statsField ?? input.stats_field,
      stats_op: input.stats_op,
      out_fields: input.out_fields,
    });
    return { chart_id: id, title, row_count: rows.length };
  },
};

async function unwrapFeatureSet(
  sourceInput: Source | string,
): Promise<{ layer_id: string; where: string; in_current_extent?: boolean }> {
  const source = parseSource(sourceInput);

  // For a handle or a spatially-filtered feature_set, the resolved OIDs already encode
  // the full filter (where + spatial_filter + extent + limit). Re-execute against that
  // exact OID set so we don't accidentally chart the entire layer.
  if (source.kind === 'handle' || (source.kind === 'feature_set' && source.spatial_filter)) {
    const ref = await resolve(source);
    if (!ref.feature_set) throw new Error('chart: source did not resolve to a feature_set');
    return materializeFeatureSet(ref.feature_set);
  }

  if (source.kind === 'feature_set') {
    return {
      layer_id: source.layer_id,
      where: source.where,
      in_current_extent: source.in_current_extent,
    };
  }

  throw new Error('chart: source must be feature_set or a feature_set handle');
}

async function materializeFeatureSet(
  fs: { layer_id: string; objectids: number[] },
): Promise<{ layer_id: string; where: string }> {
  if (fs.objectids.length === 0) return { layer_id: fs.layer_id, where: '1=0' };
  const layer = await ensureFeatureLayer(fs.layer_id);
  const oidField = layer.objectIdField || 'objectid';
  return { layer_id: fs.layer_id, where: `${oidField} IN (${fs.objectids.join(',')})` };
}

function parseSource(sourceInput: Source | string): Source {
  if (typeof sourceInput !== 'string') return sourceInput;
  try {
    const parsed = JSON.parse(sourceInput) as unknown;
    if (parsed && typeof parsed === 'object' && 'kind' in parsed) {
      return parsed as Source;
    }
  } catch {
    // fall through
  }
  throw new Error('chart: invalid source JSON string');
}
